/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

export interface RecentRecord {
  id: string;
  registrado_at: string;
  peso_kg: number | null;
  observacoes: string | null;
  condicao_geral: number | null;
}

export interface ActivePrescription {
  id: string;
  medicamento: string;
  dose: string;
  via: string;
  frequencia_h: number | null;
  inicio_at: string;
  fim_at: string | null;
  ativa: boolean;
}

export interface HospitalizationResumida {
  id: string;
  status: "ativa" | "alta" | "obito";
  entrada_at: string;
  motivo: string | null;
  pet: { id: string; name: string; birth_date: string | null };
  recentRecords: RecentRecord[];
  activePrescriptions: ActivePrescription[];
}

export interface Baia {
  id: string;
  setor_id: string;
  customer_id: string;
  nome: string;
  ordem: number;
  ativa: boolean;
  hospitalization_ativa: HospitalizationResumida | null;
}

export interface Setor {
  id: string;
  customer_id: string;
  nome: string;
  descricao: string | null;
  ordem: number;
  ativo: boolean;
  baias: Baia[];
}

async function getCustomerId(clinicId: string): Promise<string | null> {
  const { data } = await supabase
    .from("clinics")
    .select("customer_id")
    .eq("id", clinicId)
    .single();
  return (data as any)?.customer_id ?? null;
}

export function useHospitalizationSectors() {
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id;

  return useQuery({
    queryKey: ["hospitalization-setores", clinicId],
    queryFn: async () => {
      if (!clinicId) return [];
      const customerId = await getCustomerId(clinicId);
      if (!customerId) return [];

      const { data: setores, error } = await supabase
        .from("hospitalization_sectors" as any)
        .select(`
          id, customer_id, nome, descricao, ordem, ativo,
          hospitalization_bays (
            id, setor_id, customer_id, nome, ordem, ativa,
            hospitalizations (
              id, status, entrada_at, motivo,
              pets ( id, name, birth_date ),
              hospitalization_evolutions (
                id, registrado_at, peso_kg, observacoes, condicao_geral
              ),
              hospitalization_prescriptions (
                id, medicamento, dose, via, frequencia_h, inicio_at, fim_at, ativa
              )
            )
          )
        `)
        .eq("customer_id", customerId)
        .eq("ativo", true)
        .order("ordem");

      if (error) throw error;

      return ((setores as any[]) ?? []).map((s) => ({
        id: s.id,
        customer_id: s.customer_id,
        nome: s.nome,
        descricao: s.descricao,
        ordem: s.ordem,
        ativo: s.ativo,
        baias: ((s.hospitalization_bays as any[]) ?? [])
          .filter((b: any) => b.ativa)
          .sort((a: any, b: any) => a.ordem - b.ordem)
          .map((b: any) => {
            const hosp =
              ((b.hospitalizations as any[]) ?? []).find(
                (i: any) => i.status === "ativa"
              ) ?? null;

            const hospitalization_ativa: HospitalizationResumida | null = hosp
              ? {
                  id: hosp.id,
                  status: hosp.status,
                  entrada_at: hosp.entrada_at,
                  motivo: hosp.motivo ?? null,
                  pet: hosp.pets ?? { id: "", name: "?", birth_date: null },
                  recentRecords: ((hosp.hospitalization_evolutions as any[]) ?? [])
                    .sort((a: any, b: any) =>
                      new Date(b.registrado_at).getTime() - new Date(a.registrado_at).getTime()
                    )
                    .slice(0, 3)
                    .map((e: any) => ({
                      id: e.id,
                      registrado_at: e.registrado_at,
                      peso_kg: e.peso_kg ?? null,
                      observacoes: e.observacoes ?? null,
                      condicao_geral: e.condicao_geral ?? null,
                    })),
                  activePrescriptions: ((hosp.hospitalization_prescriptions as any[]) ?? [])
                    .filter((p: any) => p.ativa)
                    .map((p: any) => ({
                      id: p.id,
                      medicamento: p.medicamento,
                      dose: p.dose,
                      via: p.via,
                      frequencia_h: p.frequencia_h ?? null,
                      inicio_at: p.inicio_at,
                      fim_at: p.fim_at ?? null,
                      ativa: p.ativa,
                    })),
                }
              : null;

            return {
              id: b.id,
              setor_id: b.setor_id,
              customer_id: b.customer_id,
              nome: b.nome,
              ordem: b.ordem,
              ativa: b.ativa,
              hospitalization_ativa,
            } satisfies Baia;
          }),
      })) as Setor[];
    },
    enabled: !!clinicId,
    refetchInterval: 60_000,
  });
}

export function useCreateSetor() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (input: { nome: string; descricao?: string }) => {
      const customerId = await getCustomerId(profile!.current_clinic_id!);
      const { data, error } = await supabase
        .from("hospitalization_sectors" as any)
        .insert({ ...input, customer_id: customerId })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}

export function useCreateBaia() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (input: { setor_id: string; nome: string }) => {
      const customerId = await getCustomerId(profile!.current_clinic_id!);
      const { data, error } = await supabase
        .from("hospitalization_bays" as any)
        .insert({ ...input, customer_id: customerId })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}

export function useDeleteSetor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("hospitalization_sectors" as any)
        .update({ ativo: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}

export function useDeleteBaia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("hospitalization_bays" as any)
        .update({ ativa: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hospitalization-setores"] });
    },
  });
}

/** Calculate next dose time for a prescription */
export function nextDoseAt(p: ActivePrescription): Date | null {
  if (!p.frequencia_h) return null;
  const start = new Date(p.inicio_at).getTime();
  const now = Date.now();
  const intervalMs = p.frequencia_h * 3_600_000;
  const elapsed = now - start;
  if (elapsed < 0) return new Date(p.inicio_at);
  const n = Math.ceil(elapsed / intervalMs);
  return new Date(start + n * intervalMs);
}
